import React, { memo, useEffect, useRef, useState } from "react";

import { Box, IconButton, InputBase, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { Paragraph } from "../../../../types";

interface SubtitlesEditorProps {
  paragraphs: Paragraph[];
  onUpdateParagraph: (paragraph: Paragraph) => void;
  currentTimeInMilliseconds: number;
  onSeek: (timeInMilliseconds: number) => void;
}

const formatTimestamp = (seconds: number) => {
  const safe = Math.max(0, seconds);
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
};

interface LineProps {
  paragraph: Paragraph;
  isActive: boolean;
  activeWordIndex: number;
  onSave: (text: string) => void;
  onSeek: (timeInMilliseconds: number) => void;
  containerRef: (el: HTMLDivElement | null) => void;
}

function LineView({
  paragraph,
  isActive,
  activeWordIndex,
  onSave,
  onSeek,
  containerRef,
}: LineProps) {
  const theme = useTheme();
  const [draft, setDraft] = useState<string | null>(null);
  const isEditing = draft !== null;

  const commit = () => {
    if (draft !== null && draft.trim() && draft !== paragraph.text) {
      onSave(draft.trim());
    }
    setDraft(null);
  };

  // Seek 1ms in so the boundary doesn't resolve to the previous line.
  const seekTo = (seconds: number | null) =>
    onSeek(Math.round((seconds ?? paragraph.start) * 1000) + 1);

  return (
    <Box
      ref={containerRef}
      role="button"
      tabIndex={isEditing ? -1 : 0}
      aria-label={`Jump to ${formatTimestamp(paragraph.start)}`}
      onClick={() => {
        if (!isEditing) seekTo(paragraph.start);
      }}
      onKeyDown={(e) => {
        if (!isEditing && e.target === e.currentTarget && e.key === "Enter") {
          seekTo(paragraph.start);
        }
      }}
      sx={{
        position: "relative",
        display: "flex",
        gap: 1.25,
        cursor: isEditing ? "default" : "pointer",
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        bgcolor: isActive
          ? alpha(theme.palette.primary.main, 0.1)
          : "transparent",
        boxShadow: isActive
          ? `inset 3px 0 0 ${theme.palette.primary.main}`
          : "none",
        transition: "background-color 120ms",
        "&:hover": {
          bgcolor: isActive
            ? alpha(theme.palette.primary.main, 0.14)
            : theme.palette.action.hover,
        },
        "&:hover .line-edit": { opacity: 1 },
      }}
    >
      <Box
        component="button"
        type="button"
        tabIndex={-1}
        sx={{
          flexShrink: 0,
          width: 38,
          mt: "3px",
          p: 0,
          border: 0,
          bgcolor: "transparent",
          cursor: "pointer",
          textAlign: "left",
          fontSize: 11,
          fontVariantNumeric: "tabular-nums",
          color: isActive ? "primary.main" : "text.secondary",
          fontWeight: isActive ? 700 : 500,
          "&:hover": { color: "primary.main" },
        }}
      >
        {formatTimestamp(paragraph.start)}
      </Box>

      {isEditing ? (
        <InputBase
          autoFocus
          multiline
          fullWidth
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onFocus={(e) => {
            const input = e.target as HTMLTextAreaElement;
            input.setSelectionRange(input.value.length, input.value.length);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              commit();
            }
            if (e.key === "Escape") setDraft(null);
          }}
          sx={{
            flex: 1,
            fontSize: 14,
            lineHeight: 1.5,
            p: 0,
            px: 0.75,
            mx: -0.75,
            borderRadius: 1,
            bgcolor: "background.default",
            outline: `2px solid ${theme.palette.primary.main}`,
            "& textarea": { p: 0 },
          }}
        />
      ) : (
        <Box
          onDoubleClick={() => setDraft(paragraph.text)}
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            lineHeight: 1.5,
            color: isActive ? "text.primary" : "text.secondary",
            cursor: "pointer",
            wordBreak: "break-word",
          }}
        >
          {paragraph.words.length
            ? paragraph.words.map((w, i) => (
                <React.Fragment key={`${w.start}-${w.end}-${w.word}`}>
                  <Box
                    component="span"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      seekTo(w.start);
                    }}
                    sx={{
                      borderRadius: 0.5,
                      px: "1px",
                      ...(i === activeWordIndex && {
                        color: "primary.contrastText",
                        bgcolor: "primary.main",
                      }),
                      "&:hover": {
                        textDecoration: "underline",
                        textUnderlineOffset: "3px",
                      },
                    }}
                  >
                    {w.word}
                  </Box>{" "}
                </React.Fragment>
              ))
            : paragraph.text}
        </Box>
      )}

      {!isEditing && (
        <Tooltip title="Edit line (or double-click)">
          <IconButton
            className="line-edit"
            size="small"
            onClick={() => setDraft(paragraph.text)}
            sx={{
              opacity: 0,
              alignSelf: "flex-start",
              mt: -0.25,
              transition: "opacity 120ms",
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

const Line = memo(LineView);

export default function SubtitlesEditor({
  paragraphs,
  onUpdateParagraph,
  currentTimeInMilliseconds,
  onSeek,
}: SubtitlesEditorProps) {
  const lineRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const lastScrolledRef = useRef<string | null>(null);
  const t = currentTimeInMilliseconds / 1000;

  const activeParagraph = paragraphs.find((p) => p.start <= t && t <= p.end);
  let activeWordIndex = -1;
  activeParagraph?.words.forEach((w, i) => {
    if (w.start != null && w.start <= t) activeWordIndex = i;
  });

  useEffect(() => {
    if (!activeParagraph || lastScrolledRef.current === activeParagraph.id) {
      return;
    }
    const el = lineRefs.current[activeParagraph.id];
    const scroller = el?.closest<HTMLElement>("[data-scroll-container]");
    if (!el || !scroller) return;
    lastScrolledRef.current = activeParagraph.id;
    // The scroll container is position: relative, so offsetTop is relative to it.
    const elTop = el.offsetTop;
    const isVisible =
      elTop >= scroller.scrollTop &&
      elTop + el.offsetHeight <= scroller.scrollTop + scroller.clientHeight;
    if (!isVisible) {
      scroller.scrollTo({
        top: elTop - scroller.clientHeight / 3,
        behavior: "smooth",
      });
    }
  }, [activeParagraph]);

  if (!paragraphs.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
        No captions for this video yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", px: 1.25, pb: 1 }}
      >
        Click a word to jump to it · double-click a line to edit
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {paragraphs.map((paragraph) => {
          const isActive = paragraph.id === activeParagraph?.id;
          return (
            <Line
              key={paragraph.id}
              containerRef={(el) => {
                lineRefs.current[paragraph.id] = el;
              }}
              paragraph={paragraph}
              isActive={isActive}
              activeWordIndex={isActive ? activeWordIndex : -1}
              onSeek={onSeek}
              onSave={(text) => onUpdateParagraph({ ...paragraph, text })}
            />
          );
        })}
      </Box>
    </Box>
  );
}
